import { COMPONENT_TYPES } from '../../../constants';

/**
 * DOM에 전달되면 안 되는 커스텀 속성들을 필터링하는 함수
 */
export const filterDOMProps = (props: any) => {
  const {
    viewMode,
    emptyText,
    shippingFee,
    showStock,
    componentProps,
    data,
    content,
    skin,
    ...domProps
  } = props;
  
  return domProps;
};

/**
 * 디바이스 속성을 처리하는 유틸리티 함수
 * 디바이스 타입에 따라 적절한 속성을 병합합니다.
 */
export const applyDeviceProperties = (component: any, baseProps: any, deviceType = 'pc') => {
  // baseProps는 이미 안전한 객체이므로 shallow copy만 수행
  const deviceProps = { ...baseProps };

  // 디바이스별 스타일 적용
  if (component.style?.deviceProperty && component.style.deviceProperty[deviceType]) {
    const deviceStyle = {
      ...deviceProps.style,
      ...component.style.deviceProperty[deviceType]
    };
    
    deviceProps.style = deviceStyle;
    
    // data.style과 data.props.style도 업데이트
    deviceProps.data = {
      ...deviceProps.data,
      style: deviceStyle,
      props: {
        ...deviceProps.data.props,
        style: deviceStyle
      }
    };
  }

  // 디바이스별 componentProps 적용
  if (component.componentProps?.deviceProperty && component.componentProps.deviceProperty[deviceType]) {
    const deviceComponentProps = component.componentProps.deviceProperty[deviceType];
    
    deviceProps.componentProps = {
      ...deviceProps.componentProps,
      ...deviceComponentProps
    };
    
    // 디바이스별 className이 있으면 data에도 적용
    if (deviceComponentProps.className) {
      deviceProps.data = {
        ...deviceProps.data,
        className: deviceComponentProps.className,
        props: {
          ...deviceProps.data.props,
          className: deviceComponentProps.className
        }
      };
    }
  }

  // 디바이스별 data 적용
  if (component.data?.deviceProperty && component.data.deviceProperty[deviceType]) {
    deviceProps.data = {
      ...deviceProps.data,
      ...component.data.deviceProperty[deviceType]
    };
  }

  // 디바이스별 content 적용
  if (component.content?.deviceProperty && component.content.deviceProperty[deviceType]) {
    // content가 객체인 경우
    if (typeof component.content === 'object') {
      deviceProps.content = {
        ...deviceProps.content,
        ...component.content.deviceProperty[deviceType]
      };
    } 
    // content가 문자열인 경우
    else if (typeof component.content.deviceProperty[deviceType] !== 'undefined') {
      deviceProps.content = component.content.deviceProperty[deviceType];
    }
  }

  return deviceProps;
};

/**
 * 기본 props 생성 함수
 */
export const createBaseProps = (component: any, deviceType = 'pc') => {
  try {
    // component가 이미 안전한 복사본이라고 가정 (createComponentProps에서 처리됨)
    // 하지만 직접 호출되는 경우를 대비해 안전 검사
    const safeComponent = component || {};
    
    // 기본 props 생성
    const baseProps = {
      data: {
        ...(safeComponent.data || {}),
        // DOM 속성들을 data 객체에 추가 (스킨에서 사용하기 위해)
        id: safeComponent.id,
        className: safeComponent.componentProps?.className || '',
        style: safeComponent.style || {},
        title: safeComponent.componentProps?.title,
        emptyText: safeComponent.componentProps?.emptyText || safeComponent.emptyText,
        shippingFee: safeComponent.componentProps?.shippingFee || safeComponent.shippingFee,
        showStock: safeComponent.componentProps?.showStock || safeComponent.showStock,
        // MainBanner 등 일부 컴포넌트에서 사용하는 props 객체 추가
        props: {
          id: safeComponent.id,
          className: safeComponent.componentProps?.className || '',
          style: safeComponent.style || {}
        }
      },
      content: safeComponent.content,
      style: safeComponent.style || {},
      componentProps: {
        ...(safeComponent.componentProps || {}),
        // 스킨 속성이 componentProps 안에 포함되도록 보장
        skin: safeComponent.componentProps?.skin || safeComponent.skin,
        viewMode: deviceType
      },
      // 최상위 레벨에도 skin 추가 (호환성을 위해)
      skin: safeComponent.componentProps?.skin || safeComponent.skin
    };

    // 디바이스별 속성 적용
    const finalProps = applyDeviceProperties(safeComponent, baseProps, deviceType);
    

    return finalProps;
  } catch (error) {
    console.error('createBaseProps 오류:', error, 'component:', component);
    // 에러 시 기본 객체 반환
    return {
      data: {},
      content: '',
      style: {},
      componentProps: { viewMode: deviceType },
      skin: null
    };
  }
};

/**
 * USER_PROFILE 컴포넌트의 props 생성
 */
export const createUserProfileProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 특수 속성 처리
  const varFields = component.varFields || 
                  component.componentProps?.varFields || {
    var01: { show: false, label: "" },
    var02: { show: false, label: "" },
    var03: { show: false, label: "" }
  };
  
  // basicFields 처리
  const basicFields = component.basicFields || 
                    component.componentProps?.basicFields || {
    userId: true,
    name: true,
    password: true,
    phone: true,
    email: true,
    birthday: true,
    createdAt: true,
    address: true,
    referralLink: true
  };
  
  const title = component.content?.title || 
              component.componentProps?.title || 
              '회원 정보';
  
  // 수정된 props 구성
  return {
    ...baseProps,
    content: { 
      ...baseProps.content,
      title: title
    },
    componentProps: {
      ...baseProps.componentProps,
      title: title,
      varFields: varFields,
      basicFields: basicFields,
      skin: baseProps.componentProps?.skin  // 스킨 정보 보존
    },
    varFields: varFields,
    basicFields: basicFields,
    skin: baseProps.skin  // 최상위 스킨 정보도 보존
  };
};

/**
 * SIGNUP 컴포넌트의 props 생성
 */
export const createSignupProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 특수 속성 처리
  const varFields = component.varFields || 
                  component.componentProps?.varFields || {
    var01: { show: false, label: "" },
    var02: { show: false, label: "" },
    var03: { show: false, label: "" }
  };
  
  // basicFields 처리
  const basicFields = component.basicFields || 
                    component.componentProps?.basicFields || {
    userId: true,
    password: true,
    name: true,
    phone: true,
    email: true,
    birthday: true,
    address: true,
    referralCode: true
  };
  
  const title = component.content?.title || 
              component.componentProps?.title || 
              '회원 가입';
  
  const buttonText = component.content?.buttonText || 
                   component.componentProps?.buttonText || 
                   '가입하기';
  
  // 수정된 props 구성
  return {
    ...baseProps,
    content: { 
      ...baseProps.content,
      title: title,
      buttonText: buttonText
    },
    componentProps: {
      ...baseProps.componentProps,
      title: title,
      buttonText: buttonText,
      varFields: varFields,
      basicFields: basicFields,
      // 스킨 정보 명시적 보존
      skin: baseProps.componentProps?.skin
    },
    varFields: varFields,
    basicFields: basicFields,
    // 최상위 스킨 속성 보존
    skin: baseProps.skin
  };
};

/**
 * MEMBERSHIP_SIGNUP 컴포넌트의 props 생성
 */
export const createMembershipSignupProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 특수 속성 처리
  const title = component.componentProps?.title || 
              component.content?.title || 
              "멤버십 가입신청";
  
  const description = component.componentProps?.description || 
                    component.content?.description || 
                    "멤버십에 가입하시면 다양한 혜택을 받으실 수 있습니다.";
  
  const buttonText = component.componentProps?.buttonText || 
                   component.content?.buttonText || 
                   "가입 신청";
  
  const completedText = component.componentProps?.completedText || 
                      component.content?.completedText || 
                      "가입 완료";
  
  const productId = Number(component.componentProps?.productId) || 
                  Number(component.content?.productId) || 
                  0;
  
  // 수정된 props 구성
  return {
    ...baseProps,
    content: {
      ...baseProps.content,
      title: title,
      description: description,
      buttonText: buttonText,
      completedText: completedText,
      productId: productId
    },
    componentProps: { 
      ...baseProps.componentProps,
      className: baseProps.componentProps?.className || "membership-signup-wrapper",
      title: title,
      productId: productId,
      buttonText: buttonText,
      completedText: completedText,
      description: description
    },
    isPreview: false
  };
};

/**
 * FAQ 컴포넌트의 props 생성
 */
export const createFAQProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 특수 속성 처리
  const faqItems = component.faqItems || 
                 component.content?.faqItems || 
                 component.componentProps?.faqItems ||
                 [];
  
  const title = component.title || 
              component.content?.title || 
              component.componentProps?.title ||
              "자주하는질문";
  
  const titleStyle = component.titleStyle || 
                   component.style?.titleStyle || 
                   component.componentProps?.titleStyle ||
                   {};
  
  // 수정된 props 구성
  return {
    ...baseProps,
    id: component.id,
    title: title,
    faqItems: faqItems,
    titleStyle: titleStyle,
    content: { 
      ...baseProps.content,
      title: title,
      faqItems: faqItems
    },
    componentProps: {
      ...baseProps.componentProps,
      title: title,
      faqItems: faqItems,
      titleStyle: titleStyle
    }
  };
};

/**
 * BOARD 컴포넌트의 props 생성
 */
export const createBoardProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 버튼 표시 설정 추출
  const showNewButton = component.showNewButton !== undefined ? component.showNewButton :
                       component.componentProps?.showNewButton !== undefined ? component.componentProps.showNewButton :
                       component.data?.showNewButton !== undefined ? component.data.showNewButton : true;
  
  const showDetailButtons = component.showDetailButtons !== undefined ? component.showDetailButtons :
                          component.componentProps?.showDetailButtons !== undefined ? component.componentProps.showDetailButtons :
                          component.data?.showDetailButtons !== undefined ? component.data.showDetailButtons : true;
  
  // 디바이스별 설정 확인
  let deviceShowNewButton = true;
  
  if (component.data?.device?.[deviceType]?.showNewButton !== undefined) {
    deviceShowNewButton = component.data.device[deviceType].showNewButton;
  } else if (component.componentProps?.device?.[deviceType]?.showNewButton !== undefined) {
    deviceShowNewButton = component.componentProps.device[deviceType].showNewButton;
  }
  
  // 데이터 구성
  const boardData = {
    ...(baseProps.data || {}),
    showNewButton: deviceType === 'pc' ? showNewButton : deviceShowNewButton,
    showDetailButtons: showDetailButtons,
    device: {
      ...(baseProps.data?.device || {}),
      pc: {
        ...(baseProps.data?.device?.pc || {}),
        showNewButton: component.data?.device?.pc?.showNewButton !== undefined ? 
                       component.data.device.pc.showNewButton : showNewButton
      },
      mobile: {
        ...(baseProps.data?.device?.mobile || {}),
        showNewButton: component.data?.device?.mobile?.showNewButton !== undefined ? 
                       component.data.device.mobile.showNewButton : deviceShowNewButton
      }
    }
  };
  

  
  return {
    ...baseProps,
    data: boardData,
    type: 'BOARD',  // ComponentSkinWrapper에서 필요한 type 속성 추가
    componentProps: {
      ...baseProps.componentProps,
      // 스킨 정보 명시적으로 보존
      skin: baseProps.componentProps?.skin
    },
    // 최상위 스킨 속성도 보존
    skin: baseProps.skin
  };
};

/**
 * PRODUCT_SLIDER 컴포넌트의 props 생성  
 */
export const createProductSliderProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 스킨 정보를 여러 위치에서 확인
  const skin = component.componentProps?.skin || component.skin || baseProps.componentProps?.skin || baseProps.skin || 'basic';
  


  return {
    ...baseProps,
    componentProps: {
      ...baseProps.componentProps,
      // 스킨 정보 보존
      skin: skin
    },
    // 최상위 스킨 정보도 보존
    skin: skin
  };
};

/**
 * PRODUCT_LIST 컴포넌트의 props 생성
 */
export const createProductListProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 특수 속성 처리 - componentProps에서 상품 목록 관련 속성 추출
  const itemsPerRow = component.componentProps?.itemsPerRow || 4;
  const itemsPerPage = component.componentProps?.itemsPerPage || 20;
  const categoryId = component.componentProps?.categoryId || '';
  const showPrice = component.componentProps?.showPrice !== undefined ? component.componentProps.showPrice : true;
  const showStock = component.componentProps?.showStock !== undefined ? component.componentProps.showStock : false;
  const showAddToCart = component.componentProps?.showAddToCart !== undefined ? component.componentProps.showAddToCart : true;
  const showPagination = component.componentProps?.showPagination !== undefined ? component.componentProps.showPagination : true;
  const sortBy = component.componentProps?.sortBy || 'created';
  const sortOrder = component.componentProps?.sortOrder || 'desc';
  const priceColor = component.componentProps?.priceColor || '#ff6b6b';
  const cartButtonColor = component.componentProps?.cartButtonColor || '#007bff';
  const stockTextColor = component.componentProps?.stockTextColor || '#28a745';
  
  // 필터링 관련 속성
  const includeProductIds = component.componentProps?.include_product_ids || [];
  const excludeProductIds = component.componentProps?.exclude_product_ids || [];
  const includeCategoryIds = component.componentProps?.include_category_ids || [];
  const excludeCategoryIds = component.componentProps?.exclude_category_ids || [];
  
  return {
    ...baseProps,
    componentProps: {
      ...baseProps.componentProps,
      itemsPerRow,
      itemsPerPage,
      categoryId,
      showPrice,
      showStock,
      showAddToCart,
      showPagination,
      sortBy,
      sortOrder,
      priceColor,
      cartButtonColor,
      stockTextColor,
      include_product_ids: includeProductIds,
      exclude_product_ids: excludeProductIds,
      include_category_ids: includeCategoryIds,
      exclude_category_ids: excludeCategoryIds,
      // 스킨 정보 보존
      skin: baseProps.componentProps?.skin
    },
    // 최상위 스킨 속성도 보존
    skin: baseProps.skin
  };
};

/**
 * PAYMENT 컴포넌트의 props 생성
 */
export const createPaymentProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 스킨 정보를 여러 위치에서 확인
  const skin = component.componentProps?.skin || component.skin || baseProps.componentProps?.skin || baseProps.skin || 'basic';
  
  return {
    ...baseProps,
    componentProps: {
      ...baseProps.componentProps,
      // 스킨 정보 보존
      skin: skin
    },
    // 최상위 스킨 정보도 보존
    skin: skin
  };
};

/**
 * QUICK_MENU 컴포넌트의 props 생성
 */
export const createQuickMenuProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  return {
    ...baseProps,
    componentProps: {
      ...baseProps.componentProps,
      // QuickMenu 특수 속성들을 명시적으로 전달
      categoryItems: component.componentProps?.categoryItems || [],
      columnsPerRow: component.componentProps?.columnsPerRow || 8,
      showCategoryName: component.componentProps?.showCategoryName !== undefined 
        ? component.componentProps.showCategoryName 
        : true,
      showAllCategory: component.componentProps?.showAllCategory !== undefined
        ? component.componentProps.showAllCategory
        : true,
      allCategoryGradientStart: component.componentProps?.allCategoryGradientStart || '#667eea',
      allCategoryGradientEnd: component.componentProps?.allCategoryGradientEnd || '#764ba2',
      allCategoryTextColor: component.componentProps?.allCategoryTextColor || '#ffffff',
      imageSize: component.componentProps?.imageSize || 80,
      gap: component.componentProps?.gap || 16,
      hoverEffect: component.componentProps?.hoverEffect !== undefined 
        ? component.componentProps.hoverEffect 
        : true
    }
  };
};

/**
 * 컴포넌트 타입별 특수 props 생성
 */
export const createComponentProps = (component: any, deviceType = 'pc') => {
  // 타입 표준화
  const typeStr = String(component.type).toUpperCase();
  
  // 안전한 컴포넌트 복사본 생성 (frozen/sealed 객체 대응)
  let safeComponent;
  try {
    safeComponent = JSON.parse(JSON.stringify(component));
  } catch (error) {
    console.warn('Component 복사 실패, 기본 객체 생성:', error);
    safeComponent = { ...component, componentProps: component.componentProps || {} };
  }
  
  // componentProps가 없으면 기본값 설정 (복사본에서 안전하게)
  if (!safeComponent.componentProps) {
    safeComponent.componentProps = {};
  }
  

  
  // 컴포넌트 타입별 특수 처리
  let result;
  switch (typeStr) {
    case COMPONENT_TYPES.USER_PROFILE:
    case 'USER_PROFILE':
      result = createUserProfileProps(safeComponent, deviceType);
      break;
      
    case COMPONENT_TYPES.SIGNUP:
    case 'SIGNUP':
      result = createSignupProps(safeComponent, deviceType);
      break;
      
    case COMPONENT_TYPES.MEMBERSHIP_SIGNUP:
    case 'MEMBERSHIP_SIGNUP':
      result = createMembershipSignupProps(safeComponent, deviceType);
      break;
      
    case COMPONENT_TYPES.FAQ:
    case 'FAQ':
      result = createFAQProps(safeComponent, deviceType);
      break;
    
    case COMPONENT_TYPES.BOARD:
    case 'BOARD':
      result = createBoardProps(safeComponent, deviceType);
      break;
      
    case COMPONENT_TYPES.PRODUCT_LIST:
    case 'PRODUCT_LIST':
      result = createProductListProps(safeComponent, deviceType);
      break;
      
    case COMPONENT_TYPES.PRODUCT_SLIDER:
    case 'PRODUCT_SLIDER':
      result = createProductSliderProps(safeComponent, deviceType);
      break;
      
    case COMPONENT_TYPES.PAYMENT:
    case 'PAYMENT':
      result = createPaymentProps(safeComponent, deviceType);
      break;
      
    case COMPONENT_TYPES.QUICK_MENU:
    case 'QUICK_MENU':
      result = createQuickMenuProps(safeComponent, deviceType);
      break;
      
    case COMPONENT_TYPES.DUAL_BANNER:
    case 'DUAL_BANNER':
      // DualBanner는 기본 props 처리만으로 충분 (어댑터에서 세부 처리)
      result = createBaseProps(safeComponent, deviceType);
      break;
      
    case COMPONENT_TYPES.MAIN_BANNER:
    case 'MAIN_BANNER':
      // MainBanner는 기본 props 처리만으로 충분 (어댑터에서 세부 처리)
      result = createBaseProps(safeComponent, deviceType);
      break;
      
    // 다른 특수 컴포넌트 추가 가능
      
    default:
      // 기본 props 생성
      result = createBaseProps(safeComponent, deviceType);
  }
  

  
  return result;
}; 